importState.$inject = ['adminApi', '$interval', 'domainInfo']

export default function importState(adminApi, $interval, domainInfo) {
  let state = {
    importUuid: '',
    importRecordStatus: {},
    importRecordCommit: {},
    fetchingImportRecordStatus: false,
    fetchingImportRecordCommit: false,
    _refreshStatusPromise: null,
    applyingCommit: false,
    statusLastUpdated: '',
    autorefresh: false,
    _autorefreshInterval: 5000,
    _autorefreshPromise: null,
    _statusRefreshCallbacks: []
  }

  state.setImportUuid = function(importUuid) {
    if (this.importUuid !== importUuid) {
      this.importUuid = importUuid
      this.importRecordCommit = {}
      this.importRecordStatus = {}
      this.statusLastUpdated = ''
      this.refreshImportStatus()

      // if we have autorefresh happening, we should "clean it up"
      if (this.autorefresh) {
        this.toggleAutorefresh()
      }
    }
  }

  state.registerCallback = function(cb) {
    this._statusRefreshCallbacks.push(cb)
    return this._statusRefreshCallbacks.length - 1
  }

  state.unregisterCallback = function(index) {
    if (this._statusRefreshCallbacks[index]) {
      console.log('unregistering refresh callback at ' + index)
      this._statusRefreshCallbacks.splice(index, 1)
    }
  }

  state.refreshImportStatus = function() {
    // if refresh is already in progress, no op
    if (this.fetchingImportRecordStatus || this.fetchingImportRecordCommit) {
      return this._refreshStatusPromise
    }

    this.fetchingImportRecordStatus = true
    this._refreshStatusPromise = adminApi
      .getImportRecordStatus(this.importUuid)
      .then(resp => {
        // if the newly retrieved status shows the import as complete, and the prior update is different, then to be safe, we should clear the domainInfo details so that we don't potentially show stale data on the domain detail page
        let completedStatuses = ['completed', 'completed with errors']
        let blockingErrorStatuses = [
          'Upload data error',
          'Error analyzing changes',
          'Error',
          'Panic Error'
        ]
        if (
          completedStatuses.indexOf(resp.status) > -1 &&
          completedStatuses.indexOf(this.importRecordStatus.status) < 0
        ) {
          domainInfo.refreshUsers()
        }

        // intelligently disable autorefresh
        if (
          (completedStatuses.indexOf(resp.status) > -1 ||
            blockingErrorStatuses.indexOf(resp.status) > -1) &&
          this.autorefresh
        ) {
          this.toggleAutorefresh()
        }

        this.importRecordStatus = resp
        this.fetchingImportRecordStatus = false
        this.fetchingImportRecordCommit = true

        let commitResultPromise = adminApi.getCommitResult(this.importUuid)

        // run any callbacks
        this._statusRefreshCallbacks.forEach((cb, index) => {
          try {
            cb()
          } catch (e) {
            console.warn('Exception running import status refresh callback ' + index)
            console.warn(e)
          }
        })

        return commitResultPromise
      })
      .then(result => {
        this.importRecordCommit = result
        this.fetchingImportRecordCommit = false
        this.statusLastUpdated = Date.now()
      })
      .catch(err => {
        console.error(err)
        this.fetchingImportRecordCommit = false
        this.fetchingImportRecordStatus = false
      })

    return this._refreshStatusPromise
  }

  state.startImportDiff = function() {
    if (this.startDiffReady()) {
      adminApi.startImportDiff(this.importUuid).then(resp => {
        // intelligently enable autorefresh
        if (!this.autorefresh) {
          this.toggleAutorefresh()
        }
        return this.refreshImportStatus()
      })
    }
  }

  state.applyCommit = function() {
    this.applyingCommit = true
    adminApi.postCommit(this.importUuid).then(result => {
      this.applyingCommit = false
      // intelligently enable autorefresh
      if (!this.autorefresh) {
        this.toggleAutorefresh()
      }
      return this.refreshImportStatus()
    })
  }

  state.startDiffReady = function() {
    return (
      this.importRecordStatus &&
      this.importRecordStatus.status_log &&
      this.importRecordStatus.status_log.some(
        statusLog => statusLog.status.indexOf('uploaded and processed') >= 0
      )
    )
  }

  state.diffInProgress = function() {
    let diffStatuses = [
      'Analyzing school changes',
      'Analyzing school changes complete',
      'Loading existing users to compare changes',
      'Finished loading existing users',
      'Analyzing user changes',
      'Analyzing user changes complete',
      'Analyzing license changes',
      'Analyzing license changes complete',
      'Error analyzing changes'
    ]
    return (
      !this.previewReady() &&
      this.importRecordStatus &&
      this.importRecordStatus.status_log &&
      this.importRecordStatus.status_log.some(
        statusLog => diffStatuses.indexOf(statusLog.status) >= 0
      )
    )
  }

  state.previewReady = function() {
    return (
      this.importRecordStatus &&
      this.importRecordStatus.status_log &&
      this.importRecordStatus.status_log.some(
        statusLog => statusLog.status.indexOf('Finished analyzing changes') >= 0
      )
    )
  }

  state.importComplete = function() {
    return (
      this.importRecordStatus &&
      this.importRecordStatus.status_log &&
      this.importRecordStatus.status_log.some(
        statusLog =>
          statusLog.status === 'completed' || statusLog.status === 'completed with errors'
      )
    )
  }

  state.commitComplete = function() {
    return (
      this.importRecordCommit &&
      (this.importRecordCommit.status === 'errors' ||
        this.importRecordCommit.status === 'successful')
    )
  }

  state.commitInProgress = function() {
    return this.importRecordCommit.uuid && (this.applyingCommit || !this.commitComplete())
  }

  state.setAutorefreshInterval = function(interval) {
    if (this.autorefresh) {
      // turn off, then turn back on
      this.toggleAutorefresh(interval)
      this.toggleAutorefresh(interval)
    } else {
      this._autorefreshInterval = interval
    }
  }

  state.toggleAutorefresh = function(interval) {
    if (interval) {
      this._autorefreshInterval = interval
    }
    this.autorefresh = !this.autorefresh
    if (this.autorefresh) {
      console.log('Enabling autorefresh')
      this._autorefreshPromise = $interval(
        this.refreshImportStatus.bind(this),
        this._autorefreshInterval || 5000
      )
    } else {
      console.log('Canceling autorefresh')
      $interval.cancel(this._autorefreshPromise)
      this._autorefreshPromise = null
    }
  }

  state.autorefreshIntervalSeconds = function() {
    return this._autorefreshInterval / 1000
  }

  return state
}
